import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import PublicSite from "@/pages/PublicSite";
import Admin from "@/pages/Admin";
import CeoPortal from "@/pages/CeoPortal";
import AcceptInvite from "@/pages/AcceptInvite";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import InstallPrompt from "@/components/InstallPrompt";
import { registerServiceWorker } from "@/lib/pwa";

// Standard ecommerce template — first reusable industry-aware preset stack.
import TemplateLayout from "@/pages/template-ecommerce/TemplateLayout";
import TemplateHome from "@/pages/template-ecommerce/Home";
import TemplateShop from "@/pages/template-ecommerce/Shop";
import TemplateProductDetail from "@/pages/template-ecommerce/ProductDetail";
import TemplateCheckout from "@/pages/template-ecommerce/Checkout";
import TemplateOrderSuccess from "@/pages/template-ecommerce/OrderSuccess";
import PublicEcommerceShell from "@/pages/PublicEcommerceShell";

function AppRouter() {
  const location = useLocation();
  // Detect session_id in URL hash synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/ceo" element={<CeoPortal />} />
      <Route path="/team/invite/:token" element={<AcceptInvite />} />

      {/* Published store routes — each fetches site data once via
          PublicEcommerceShell, mounts the standard ecommerce template chrome
          with the backend-built ecommerce_config as overrides, then renders
          the matched Page component inside. */}
      <Route path="/site/:slug" element={<PublicSite />} />
      <Route path="/site/:slug/shop" element={<PublicEcommerceShell Page={TemplateShop} />} />
      <Route path="/site/:slug/product/:productSlug" element={<PublicEcommerceShell Page={TemplateProductDetail} />} />
      <Route path="/site/:slug/checkout" element={<PublicEcommerceShell Page={TemplateCheckout} />} />
      {/* `order/success` is the Stripe-Connect redirect target. The template
          OrderSuccess polls the session by ?session_id=… and renders the
          order under the same chrome. Static match wins over the dynamic
          `:orderNumber` route below. */}
      <Route path="/site/:slug/order/success" element={<PublicEcommerceShell Page={TemplateOrderSuccess} />} />
      <Route path="/site/:slug/order/:orderNumber" element={<PublicEcommerceShell Page={TemplateOrderSuccess} />} />
      <Route path="/site/:slug/:pageSlug" element={<PublicSite />} />

      {/* Standard ecommerce template — reusable per-industry preset routes. */}
      <Route path="/template/:industryKey" element={<TemplateLayout />}>
        <Route index element={<TemplateHome />} />
        <Route path="shop" element={<TemplateShop />} />
        <Route path="product/:productSlug" element={<TemplateProductDetail />} />
        <Route path="checkout" element={<TemplateCheckout />} />
        <Route path="order/:orderNumber" element={<TemplateOrderSuccess />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  useEffect(() => { registerServiceWorker(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <InstallPrompt />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
